// src/components/modal/tabs/VariantTabContent.tsx
import React, { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { FaPlus, FaTimes } from "react-icons/fa";
import type { MenuItem, Branch } from "~/types/new_types";
import { getItemName, getFormName } from "~/utils/menuItemHelper";
import { InlineVariantCustomization } from "../InlineVariantCustomization";
import { useBranch } from "~/contexts/BranchContext";

/**
 * Helper function to get localized option field
 */
const getLocalizedField = (
  optionData: any,
  field: string,
  language: string
): string => {
  // Check if Arabic and ar object exists with the field
  if (language === "ar" && optionData?.ar?.[field]) {
    return optionData.ar[field];
  }
  // Fallback to default field
  return optionData?.[field] || "";
};

interface ItemCard {
  id: string;
  name: string;
  variants: Record<string, any>;
  quantity: number;
}

interface VariantTabContentProps {
  item: MenuItem;
  theme:
  | Branch["theme"]
  | {
    primaryColor?: string;
    secondaryColor?: string;
    backgroundColor?: string;
    cardColor?: string;
    textColor?: string;
    borderColor?: string;
    borderRadius?: string;
    fontFamily?: string;
  };
  language: string;
  itemCards: ItemCard[];
  onCardClick?: (cardIndex: number) => void;
  currentQuantity?: number;
  updateCardVariant?: (
    cardIndex: number,
    optionName: string,
    value: any
  ) => void;
  updateCardQuantity?: (cardIndex: number, quantity: number) => void;
  onPopupDone?: (selectedCardIndex: number, popupQuantity: number) => void;
  onAddItemCard?: () => void;
  onRemoveItemCard?: (cardIndex: number) => void;
}

export const VariantTabContent: React.FC<VariantTabContentProps> = ({
  item,
  theme,
  language,
  itemCards,
  onCardClick,
  currentQuantity = 0,
  updateCardVariant,
  updateCardQuantity,
  onAddItemCard,
  onRemoveItemCard,
}) => {
  const { t, i18n } = useTranslation();
  const { menuItems } = useBranch();

  const primaryColor = theme?.primaryColor || "#ea580c";
  const textColor = theme?.textColor || "#1f2937";

  const menuItemMap = useMemo(() => {
    const map = new Map<string, MenuItem>();
    if (Array.isArray(menuItems)) {
      menuItems.forEach((menuItem) => {
        if (menuItem?.id) {
          map.set(menuItem.id, menuItem as MenuItem);
        }
      });
    }
    return map;
  }, [menuItems]);

  // Local state for selected card for inline customization
  const [selectedCardIndex, setSelectedCardIndex] = useState<number | null>(
    itemCards.length > 0 ? 0 : null
  );

  const variantGroupLookup = useMemo(() => {
    const map = new Map<
      string,
      {
        formName: string;
        isMultiSelection: boolean;
      }
    >();

    if (!Array.isArray(item.variants_and_options)) {
      return map;
    }

    item.variants_and_options.forEach((group) => {
      // Only process groups that have array data (variant options, not combo data)
      const data = group.data;

      // Skip if data is not an array (combo data should be objects with items array)
      if (!Array.isArray(data)) {
        return;
      }

      // Additional check: ensure this is actual variant options and not nested combo items
      const isActualVariantOptions = data.every(
        (item: any) =>
          typeof item === "object" &&
          "name" in item &&
          "price" in item &&
          !("items" in item) // Not a combo structure
      );

      if (!isActualVariantOptions) {
        return;
      }

      const meta = {
        formName: getFormName(group, i18n.language) || "Options",
        isMultiSelection: Boolean((group as any).is_multiselection),
      };

      if (group.id) {
        map.set(group.id, meta);
      }

      if (group.form_name) {
        map.set(group.form_name, meta);
      }
    });

    return map;
  }, [item.variants_and_options, i18n.language]);

  const comboGroupLookup = useMemo(() => {
    const map = new Map<string, { formName: string }>();

    if (Array.isArray(item.meals_and_combos)) {
      item.meals_and_combos.forEach((group) => {
        const id = group.id || group.form_name;
        if (!id) return;
        map.set(id, { formName: getFormName(group, i18n.language) || "Combo" });
      });
    }

    if (Array.isArray(item.variants_and_options)) {
      item.variants_and_options.forEach((group) => {
        const comboData = group.data;
        const isCombo =
          comboData &&
          !Array.isArray(comboData) &&
          typeof comboData === "object" &&
          Array.isArray((comboData as any).items);

        if (!isCombo) return;

        const id = group.id || group.form_name;
        if (!id) return;

        map.set(id, { formName: getFormName(group, i18n.language) || "Combo" });
      });
    }

    return map;
  }, [item.meals_and_combos, item.variants_and_options, i18n.language]);



  // Update selected card when itemCards change
  React.useEffect(() => {
    if (itemCards.length > 0 && selectedCardIndex === null) {
      setSelectedCardIndex(0);
    }
  }, [itemCards.length, selectedCardIndex]);

  const handleCardClick = (cardIndex: number) => {
    setSelectedCardIndex(cardIndex);
    if (onCardClick) {
      onCardClick(cardIndex);
    }
  };

  const handleQuantityChange = (cardIndex: number, newQuantity: number) => {
    // Use the proper updateCardQuantity function from useItemCards hook
    if (updateCardQuantity) {
      updateCardQuantity(cardIndex, newQuantity);
    } else {
    }
  };

  const hasVariantGroups = variantGroupLookup.size > 0;
  const hasComboGroups = comboGroupLookup.size > 0;
  const hasAnyCustomization = hasVariantGroups || hasComboGroups;

  const previewVariantGroups = useMemo(() => {
    if (!Array.isArray(item.variants_and_options)) {
      return [] as Array<{
        id: string;
        formName: string;
        isMultiSelection: boolean;
        maxSelection: number;
        options: any[];
      }>;
    }

    return item.variants_and_options
      .map((group) => {
        // Only process groups that have array data (variant options, not combo data)
        const data = group.data;

        // Skip if data is not an array (combo data should be objects with items array)
        if (!Array.isArray(data)) {
          return null;
        }

        // Additional check: ensure this is actual variant options and not nested combo items
        const isActualVariantOptions = data.every(
          (item: any) =>
            typeof item === "object" &&
            "name" in item &&
            "price" in item &&
            !("items" in item) // Not a combo structure
        );

        if (!isActualVariantOptions) {
          return null;
        }

        const id = group.id || group.form_name;
        if (!id) {
          return null;
        }

        return {
          id,
          formName: getFormName(group, i18n.language) || "Options",
          isMultiSelection: Boolean((group as any).is_multiselection),
          maxSelection: Number((group as any).max_selection) || 0,
          options: data,
        };
      })
      .filter(Boolean) as Array<{
        id: string;
        formName: string;
        isMultiSelection: boolean;
        maxSelection: number;
        options: any[];
      }>;
  }, [item.variants_and_options, i18n.language]);

  const previewComboGroups = useMemo(() => {
    const combos: Array<{
      id: string;
      formName: string;
      comboPrice: number;
      items: Array<{ name: string; quantity: number }>;
    }> = [];

    const resolveComboItems = (comboData: any) => {
      const items = Array.isArray(comboData?.items) ? comboData.items : [];
      return items
        .map((comboItem: any) => {
          const quantity = Number(comboItem?.quantity) || 1;
          const explicitName = comboItem?.item_name || comboItem?.name;

          let resolvedName = explicitName;
          if (!resolvedName) {
            const itemId = comboItem?.item_id || comboItem?.itemId;
            if (typeof itemId === "string") {
              resolvedName = menuItemMap.get(itemId)?.name || itemId;
            }
          }

          if (!resolvedName) {
            return null;
          }

          return {
            name: resolvedName,
            quantity,
          };
        })
        .filter(Boolean) as Array<{ name: string; quantity: number }>;
    };

    if (Array.isArray(item.meals_and_combos)) {
      item.meals_and_combos.forEach((group) => {
        const comboData: any = group.data;
        if (!comboData) return;

        const items = resolveComboItems(comboData);
        if (items.length === 0) return;

        combos.push({
          id:
            group.id ||
            group.form_name ||
            `combo-preview-${combos.length}-${Math.random()
              .toString(36)
              .slice(2)}`,
          formName: getFormName(group, i18n.language) || "Combo",
          comboPrice:
            Number(
              (comboData as any).combo_price || (comboData as any).price
            ) || 0,
          items,
        });
      });
    }

    if (Array.isArray(item.variants_and_options)) {
      item.variants_and_options.forEach((group) => {
        const comboData: any = group.data;
        const isCombo =
          comboData &&
          !Array.isArray(comboData) &&
          typeof comboData === "object" &&
          Array.isArray((comboData as any).items);

        if (!isCombo) return;

        const items = resolveComboItems(comboData);
        if (items.length === 0) return;

        combos.push({
          id:
            group.id ||
            group.form_name ||
            `combo-preview-${combos.length}-${Math.random()
              .toString(36)
              .slice(2)}`,
          formName: getFormName(group, i18n.language)
            ? `${getFormName(group, i18n.language)} (Combo)`
            : "Combo",
          comboPrice:
            Number(
              (comboData as any).combo_price || (comboData as any).price
            ) || 0,
          items,
        });
      });
    }

    return combos;
  }, [
    item.meals_and_combos,
    item.variants_and_options,
    menuItemMap,
    i18n.language,
  ]);

  if (!hasAnyCustomization) {
    // For items with variants, load quantity from session storage


    return (
      <div className="space-y-6">
        <div
          className="flex flex-col items-center justify-center py-8 text-center"
          style={{ color: `${theme?.textColor}60` }}
        >
          {/* <div className="text-4xl mb-4">🍽️</div> */}
          <p
            className="text-lg font-medium mb-2"
            style={{ color: theme?.textColor }}
          >
            {getItemName(item, language)}
          </p>
          <p className="text-sm mb-4">{t("menuItem.noCustomization")}</p>
          {/* <p className="text-xs" style={{ color: `${theme?.textColor}80` }}>
            {t("menu.addToOrder") ?? t("menu.addToOrder")}
          </p> */}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {itemCards.length > 0 ? (
        <div className="space-y-6">
          {/* Item Cards */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3
                className="text-lg font-semibold"
                style={{ color: theme?.textColor }}
              >
                {t("menu.itemCards")} ({itemCards.length})
              </h3>

              {/* Plus Control */}
              {onAddItemCard && (
                <div className="flex items-center gap-2">
                  <motion.button
                    onClick={onAddItemCard}
                    className="w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all"
                    style={{
                      backgroundColor: theme?.cardColor,
                      borderColor: theme?.primaryColor,
                    }}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <FaPlus size={12} style={{ color: theme?.primaryColor }} />
                  </motion.button>
                </div>
              )}
            </div>

            {/* Card Selection Tabs */}
            <div
              className="flex gap-2 mb-4 py-4 px-2 overflow-x-auto space-x-2"
              style={{
                borderBottom: `2px solid ${theme?.borderColor}50`,
              }}
            >
              {itemCards.map((card, index) => (
                <div key={card.id} className="relative">
                  <button
                    onClick={() => handleCardClick(index)}
                    className="px-4 py-2 rounded-lg border whitespace-nowrap transition-all"
                    style={{
                      backgroundColor:
                        selectedCardIndex === index
                          ? theme?.primaryColor
                          : theme?.cardColor,
                      color:
                        selectedCardIndex === index ? "#fff" : theme?.textColor,
                      borderColor:
                        selectedCardIndex === index
                          ? theme?.primaryColor
                          : theme?.borderColor,
                      borderRadius: theme?.borderRadius,
                    }}
                  >
                    {t("menu.item")} {index + 1}
                    {card.quantity > 1 && ` (${card.quantity}x)`}
                  </button>

                  {/* Delete button - only show if more than 1 card */}
                  {itemCards.length > 1 && (
                    <motion.button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (onRemoveItemCard) {
                          onRemoveItemCard(index);
                        }
                      }}
                      className="bg-red-500 absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center border-1"

                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <FaTimes size={10} />
                    </motion.button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Inline Variant Customization */}
          {selectedCardIndex !== null && updateCardVariant && (
            <InlineVariantCustomization
              item={item}
              theme={theme}
              itemCards={itemCards}
              selectedCardIndex={selectedCardIndex}
              onVariantUpdate={updateCardVariant}
              onQuantityChange={handleQuantityChange}
            />
          )}
        </div>
      ) : (
        <div className="space-y-6">
          {(previewVariantGroups.length > 0 ||
            previewComboGroups.length > 0) && (
              <div className="space-y-8">
                {previewVariantGroups.length > 0 && (
                  <div className="space-y-4">
                    <h3
                      className="text-lg font-semibold"
                      style={{ color: theme?.textColor }}
                    >
                      {t("menu.availableVariants", {
                        defaultValue: "Available Variants",
                      })}
                    </h3>

                    {previewVariantGroups.map((group) => (
                      <div
                        key={group.id}
                        className="space-y-2"
                      >
                        {/* Group Header */}
                        <div className="flex items-center justify-between mb-1">
                          <p
                            className="text-base font-semibold"
                            style={{ color: theme?.textColor }}
                          >
                            {group.formName}
                          </p>
                          {group.isMultiSelection && (
                            <span
                              className="text-xs font-medium px-2 py-1 rounded-full"
                              style={{
                                color: theme?.primaryColor,
                                backgroundColor: `${theme?.primaryColor}15`,
                              }}
                            >
                              {group.maxSelection > 0
                                ? t("menu.selectUpTo", {
                                  defaultValue: "Select up to {{count}}",
                                  count: group.maxSelection,
                                })
                                : t("menu.multiSelection", {
                                  defaultValue: "Multiple selection",
                                })}
                            </span>
                          )}
                        </div>

                        {/* Inline Options List */}
                        <div className="flex flex-col">
                          {group.options.map(
                            (option: any, optionIndex: number) => (
                              <div
                                key={`${group.id}-${optionIndex}`}
                                className="flex items-center gap-3 py-2 px-1"
                              >

                                {/* Option details */}
                                <div className="flex-1 min-w-0">
                                  <span
                                    className="text-sm font-medium"
                                    style={{ color: theme?.textColor }}
                                  >
                                    {getLocalizedField(
                                      option,
                                      "name",
                                      i18n.language
                                    )}
                                  </span>
                                  {getLocalizedField(
                                    option,
                                    "note",
                                    i18n.language
                                  ) && (
                                      <p
                                        className="text-xs mt-0.5 opacity-60"
                                        style={{ color: theme?.textColor }}
                                      >
                                        {getLocalizedField(
                                          option,
                                          "note",
                                          i18n.language
                                        )}
                                      </p>
                                    )}
                                </div>

                                {/* Price */}
                                {Number(option.price) > 0 && (
                                  <span
                                    className="text-sm font-medium flex-shrink-0"
                                    style={{ color: theme?.primaryColor }}
                                  >
                                    + ${Number(option.price).toFixed(2)}
                                  </span>
                                )}
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {previewComboGroups.length > 0 && (
                  <div className="space-y-4">
                    <h3
                      className="text-lg font-semibold"
                      style={{ color: theme?.textColor }}
                    >
                      {t("menu.availableCombos", {
                        defaultValue: "Available Combos",
                      })}
                    </h3>

                    {previewComboGroups.map((combo) => (
                      <div
                        key={combo.id}
                        className="space-y-2"
                      >
                        {/* Combo Header */}
                        <div className="flex items-center justify-between mb-1">
                          <p
                            className="text-base font-semibold"
                            style={{ color: theme?.textColor }}
                          >
                            {combo.formName}
                          </p>
                          {combo.comboPrice > 0 && (
                            <span
                              className="text-sm font-semibold px-2 py-1 rounded-full"
                              style={{
                                color: theme?.primaryColor,
                                backgroundColor: `${theme?.primaryColor}15`,
                              }}
                            >
                              ${combo.comboPrice.toFixed(2)}
                            </span>
                          )}
                        </div>

                        {/* Combo Items - Inline List */}
                        <div className="flex flex-col">
                          {combo.items.map((comboItem, itemIndex) => (
                            <div
                              key={`${combo.id}-${itemIndex}`}
                              className="flex items-center gap-3 py-2 px-1"
                            >

                              {/* Item name */}
                              <div className="flex-1 min-w-0">
                                <span
                                  className="text-sm font-medium"
                                  style={{ color: theme?.textColor }}
                                >
                                  {comboItem.name}
                                </span>
                              </div>

                              {/* Quantity */}
                              <span
                                className="text-sm font-medium flex-shrink-0 opacity-60"
                                style={{ color: theme?.textColor }}
                              >
                                x{comboItem.quantity}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

          {/* Only show "Customize Your Item" prompt when no variants/combos are available */}
          {previewVariantGroups.length === 0 &&
            previewComboGroups.length === 0 && (
              <div
                className="flex flex-col items-center justify-center py-12 text-center"
                style={{ color: `${theme?.textColor}80` }}
              >
                <div className="text-4xl mb-4">🎨</div>
                <p
                  className="text-lg font-medium mb-2"
                  style={{ color: theme?.textColor }}
                >
                  {t("menu.customizeYourItem", {
                    defaultValue: "Customize Your Item",
                  })}
                </p>
                <p className="text-sm mb-6">
                  {t("menu.addCardToStartCustomizing", {
                    defaultValue:
                      "Click the + button above to add a card and start customizing",
                  })}
                </p>
              </div>
            )}
        </div>
      )}
    </div>
  );
};
